/**
 * NotificationsPanel
 *
 * Popover MUI anclado al Bell del Header. Muestra tabs por kind + lista de
 * `NotificationItem` + acción de marcar todas como leídas.
 *
 * Vista "Todas" agrupa por kind con separadores visuales.
 * Vistas por tab filtran por kind. Tabs con count=0 se ocultan.
 */

import { useMemo, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';
import {
  useNotificationsStore,
  type AppNotification,
  type NotificationKind,
} from '@/store/notificationsStore';
import { NotificationItem } from './NotificationItem';

type TabValue = 'all' | NotificationKind;

interface NotificationsPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const TAB_LABEL: Record<TabValue, string> = {
  all: 'Todas',
  enrollment: 'Enrollments',
  chat: 'Chats',
  update: 'Updates',
  'update-failed': 'Updates',
};

function tabFromKind(kind: NotificationKind): TabValue {
  return kind === 'update' || kind === 'update-failed' ? 'update' : kind;
}

function visibleTabs(
  counts: Record<TabValue, number>
): Array<{ value: TabValue; label: string; hidden: boolean }> {
  return [
    { value: 'all', label: TAB_LABEL.all, hidden: false },
    { value: 'enrollment', label: TAB_LABEL.enrollment, hidden: counts.enrollment === 0 },
    { value: 'chat', label: TAB_LABEL.chat, hidden: counts.chat === 0 },
    {
      value: 'update',
      label: TAB_LABEL.update,
      hidden: counts.update === 0 && counts['update-failed'] === 0,
    },
  ];
}

export function NotificationsPanel({ open, anchorEl, onClose }: NotificationsPanelProps) {
  const items = useNotificationsStore((s) => s.items);
  const unreadTotal = useNotificationsStore((s) => s.unreadTotal);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const markRead = useNotificationsStore((s) => s.markRead);

  const [tab, setTab] = useState<TabValue>('all');

  const counts = useMemo(() => {
    const c: Record<TabValue, number> = {
      all: 0,
      enrollment: 0,
      chat: 0,
      update: 0,
      'update-failed': 0,
    };
    for (const n of items) {
      if (!n.read) {
        c.all += 1;
        c[n.kind] += 1;
        if (n.kind === 'update' || n.kind === 'update-failed') {
          c.update += 1;
        } else {
          c[n.kind === 'enrollment' ? 'enrollment' : n.kind] += 0; // noop for clarity
        }
      }
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (tab === 'all') return items;
    return items.filter((n) => {
      const tk = tabFromKind(n.kind);
      return tk === tab;
    });
  }, [items, tab]);

  // Agrupar por kind para los separadores en "Todas".
  const grouped = useMemo(() => {
    if (tab !== 'all') return null;
    const order: NotificationKind[] = ['enrollment', 'chat', 'update', 'update-failed'];
    const byKind: Record<NotificationKind, AppNotification[]> = {
      enrollment: [],
      chat: [],
      update: [],
      'update-failed': [],
    };
    for (const n of filtered) byKind[n.kind].push(n);
    const sections: Array<{ kind: NotificationKind; items: AppNotification[]; label: string }> = [];
    for (const k of order) {
      if (byKind[k].length === 0) continue;
      sections.push({
        kind: k,
        items: byKind[k]!,
        label:
          k === 'enrollment'
            ? 'ENROLLMENTS'
            : k === 'chat'
              ? 'CHATS'
              : k === 'update'
                ? 'UPDATES'
                : 'UPDATES FALLIDOS',
      });
    }
    return sections;
  }, [filtered, tab]);

  const tabs = useMemo(() => visibleTabs(counts), [counts]);

  const handleItemAction = (n: AppNotification) => {
    markRead(n.id);
    onClose();
  };

  const id = open ? 'notifications-popover' : undefined;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            maxWidth: 380,
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 600 }}>
          Notificaciones
        </Typography>
        <IconButton
          size="small"
          aria-label="cerrar"
          onClick={onClose}
          data-testid="notifications-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v: TabValue) => setTab(v)}
        variant="fullWidth"
        sx={{ minHeight: 36 }}
        slotProps={{
          indicator: { sx: { height: 2 } },
        }}
      >
        {tabs
          .filter((t) => !t.hidden)
          .map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={
                t.value === 'all'
                  ? `Todas${counts.all > 0 ? ` (${counts.all})` : ''}`
                  : t.value === 'update'
                    ? `Updates (${counts.update})`
                    : t.value === 'enrollment'
                      ? `Enrollments (${counts.enrollment})`
                      : `Chats (${counts.chat})`
              }
              data-testid={`notifications-tab-${t.value}`}
              sx={{ minHeight: 36, fontSize: '0.78rem', textTransform: 'none' }}
            />
          ))}
      </Tabs>
      <Divider />

      {/* Body */}
      <Box
        sx={{
          maxHeight: 420,
          overflowY: 'auto',
        }}
        data-testid="notifications-list"
      >
        {filtered.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 2,
              color: 'text.secondary',
            }}
            data-testid="notifications-empty"
          >
            <NotificationsNoneIcon sx={{ fontSize: 48, mb: 1, opacity: 0.6 }} />
            <Typography variant="body2" fontWeight={600}>
              Estás al día
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5 }} align="center">
              No hay notificaciones {tab === 'all' ? '' : `de ${TAB_LABEL[tab].toLowerCase()}`} por
              ahora.
            </Typography>
          </Box>
        ) : tab === 'all' && grouped ? (
          <Box>
            {grouped.map((section, idx) => (
              <Box key={section.kind} data-testid={`notifications-section-${section.kind}`}>
                {idx > 0 && <Divider />}
                <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    {section.label}
                  </Typography>
                </Box>
                {section.items.map((n) => (
                  <NotificationItem key={n.id} notification={n} onAction={handleItemAction} />
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            {filtered.map((n) => (
              <NotificationItem key={n.id} notification={n} onAction={handleItemAction} />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      {unreadTotal > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            <Button
              size="small"
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
              sx={{ textTransform: 'none' }}
            >
              Marcar todas como leídas
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
}

export default NotificationsPanel;
